import { createFileRoute, useNavigate } from "@tanstack/react-router";

import {
  communityListFilterNavigation,
  communityListPageNavigation,
  type CommunityListSearchParams,
} from "@/api/contracts/communities";
import { CommunitiesListPage } from "@/components/communities/communities-list-page";
import { requireInternalAudience } from "@/lib/route-guards";

// The communities list — the internal landing, and modsy's effective home:
// `/`, post-login forwarding and the `/dashboard` bookmark all arrive here
// (`App.tsx:84,108`). In a `communities/` folder so the details route (#751)
// joins it as a sibling, the shape `items/` and `specifications/` have.
//
// **Gated by audience only**, as the source's route checks sign-in only
// (`App.tsx:112`). A capability gate would bounce a session without the
// capability to the internal landing — this page — and loop. Such a session
// (a migrator-only or roleless one) renders the page, and the read's own
// `canBrowseSpecs` refusal draws the first-load error state.

/**
 * The URL's params exactly as the router's codec read them (`parseAppSearch`):
 * the page parses them itself, so it still holds the raw values — an off-menu
 * `pageSize`, an unknown `status`, a param it does not own — when it decides
 * whether the address bar needs canonicalizing.
 */
function validateSearch(search: Record<string, unknown>): CommunityListSearchParams {
  return search;
}

export const Route = createFileRoute("/_authed/_chrome/communities/")({
  validateSearch,
  beforeLoad: ({ context }) => {
    requireInternalAudience(context.currentUser.user);
  },
  // The source's `useSetPageTitle('Communities')`, without its product suffix.
  head: () => ({ meta: [{ title: "Communities" }] }),
  component: CommunitiesListRoute,
});

function CommunitiesListRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  return (
    <CommunitiesListPage
      search={search}
      // A filter or division change: page 1, replace — the rule is the
      // contract's, so the routing test runs it.
      onFilterChange={(patch) => {
        void navigate(communityListFilterNavigation(patch));
      }}
      // The pager, the past-the-end clamp, and the arrival canonicalization —
      // each a replace that keeps the filters and drops every param the page
      // does not own.
      onPageChange={(window) => {
        void navigate(communityListPageNavigation(window));
      }}
    />
  );
}
